import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import { Box, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material'

import { useMonthLabel } from '../../hooks/useMonthLabel'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsActions, useUAInsightsScope } from '../../hooks/useUAInsightsNavigation'

interface UAInsightsMonthSelectorProps {
  size?: 'small' | 'medium'
  label?: string
  sx?: object
  /** When true, the underlying FormControl is greyed-out and unclickable. */
  disabled?: boolean
}

/**
 * Month filter for UA Insights. Options are derived from the dataset's
 * `meta.months` and the selection persists via the navigation context's
 * `month` URL param so every dashboard hook reacts to it.
 */
export const UAInsightsMonthSelector: FC<UAInsightsMonthSelectorProps> = ({ size = 'small', label, sx, disabled = false }) => {
  const { t } = useTranslation('ua-insights')
  const { month } = useUAInsightsScope()
  const { setMonth } = useUAInsightsActions()

  const { months } = useUAInsightsMeta()
  const monthLabel = useMonthLabel()
  const effectiveLabel = label ?? t('month_filter_label')
  const selectValue = month && months.includes(month) ? month : months[months.length - 1] ?? ''

  if (months.length === 0) return null

  const handleChange = (event: SelectChangeEvent<string>) => {
    setMonth(event.target.value || null)
  }

  return (
    <FormControl size={size} disabled={disabled} sx={{ minWidth: 180, ...sx }}>
      <Select
        value={selectValue}
        onChange={handleChange}
        color="primary"
        inputProps={{ 'aria-label': effectiveLabel }}
        renderValue={(value) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalendarMonthRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <span>{monthLabel(value)}</span>
          </Box>
        )}
        sx={{
          borderRadius: 999,
          backgroundColor: 'background.paper',
        }}
      >
        {months.map((m) => (
          <MenuItem key={m} value={m}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarMonthRoundedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <span>{monthLabel(m)}</span>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
